import { z } from 'zod';

/**
 * Light / Dark / System (brand/README.md "Light & dark mode"). The tokens in brand/tokens.css switch on
 * `data-theme` on <html>. The inline script in index.html applies the saved choice before first paint;
 * this module keeps it in sync afterwards. No hex values here: the browser bar colour is read from the
 * `--bg` token once the stylesheet is loaded.
 */
export const THEME_PREFERENCES = ['light', 'dark', 'system'] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];
export type Theme = 'light' | 'dark';

export const ThemePreferenceSchema = z.enum(THEME_PREFERENCES);

/** The local copy of the choice. index.html reads the same key. */
export const THEME_STORAGE_KEY = 'fundline.theme';

export const DARK_QUERY = '(prefers-color-scheme: dark)';

/** A saved value from storage or the account; anything unknown means System. */
export function parsePreference(raw: unknown): ThemePreference {
  const parsed = ThemePreferenceSchema.safeParse(raw);
  return parsed.success ? parsed.data : 'system';
}

export function resolveTheme(preference: ThemePreference, systemDark: boolean): Theme {
  if (preference === 'system') return systemDark ? 'dark' : 'light';
  return preference;
}

/** Storage can be unavailable (private mode, blocked site data): fall back to System. */
export function readStoredPreference(storage: Pick<Storage, 'getItem'> | null): ThemePreference {
  try {
    return parsePreference(storage?.getItem(THEME_STORAGE_KEY));
  } catch {
    return 'system';
  }
}

export function writeStoredPreference(
  storage: Pick<Storage, 'setItem'> | null,
  preference: ThemePreference,
): void {
  try {
    storage?.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // The account copy still holds the choice; it is applied again after the next sign-in.
  }
}

export function browserStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

/** Sets `data-theme` and points the mobile browser bar at the theme's `bg`. */
export function applyTheme(theme: Theme, doc: Document = document): void {
  const root = doc.documentElement;
  if (root.dataset.theme !== theme) root.dataset.theme = theme;
  // index.html paints the page background inline before the stylesheet; the token takes over now.
  root.style.removeProperty('background-color');
  const bg = getComputedStyle(root).getPropertyValue('--bg').trim();
  const meta = doc.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta && bg) meta.content = bg;
}
